<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  size: {
    type: String,
    default: "md",
  },
  src: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "GR",
  },
  cursor: {
    type: String,
    default: "default",
  },
  isAmbassador: {
    type: Boolean,
    default: false,
  },
});

const loaded = ref(false);

const initials = computed(() => {
  // função para remover caracteres especiais
  function cleanName(name) {
    return name.replace(/[^a-zA-ZÀ-ÿ]/g, "");
  }

  const names = props.placeholder
    .trim()
    .split(" ")
    .map(cleanName)
    .filter((name) => name.length > 0);

  // pega a primeira letra do nome e do sobrenome
  if (names.length >= 2) {
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  }

  // se tiver apenas um nome, pega as duas primeiras letras desse nome
  const singleName = cleanName(props.placeholder);
  return singleName.slice(0, 2).toUpperCase();
});

function imageLoad() {
  loaded.value = true;
}
</script>

<template>
  <div class="avatar" :class="isAmbassador ? 'ambassador-avatar' : ''">
    <div
      v-if="!loaded || !src"
      class="initials photo"
      :class="`avatar-${size}`"
      :style="{
        cursor: cursor,
        position: src ? 'absolute' : 'relative',
      }"
    >
      {{ initials ? initials : "GR" }}
    </div>

    <img
      v-if="src"
      :src="src"
      :alt="placeholder"
      class="photo no-photo"
      :class="`avatar-${size}`"
      :style="{ cursor: cursor }"
      loading="lazy"
      @load="imageLoad"
    />

    <svg v-if="isAmbassador" width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.29677 15.69L5.49133 16.4143L6.04677 16.2651V15.69H5.29677ZM2 16.5755L1.34981 16.2017L0.448803 17.7688L2.19456 17.2999L2 16.5755ZM5.05169 11.2678L5.52992 10.6901L4.8448 10.123L4.4015 10.894L5.05169 11.2678ZM8.82892 14.3943L9.47911 14.7682L9.79438 14.2199L9.30715 13.8166L8.82892 14.3943ZM6.18154 18.9986L5.45698 19.1923L5.92613 20.9474L6.83172 19.3725L6.18154 18.9986ZM5.29677 15.6886L6.02133 15.4949L5.82734 14.7692L5.10192 14.9644L5.29677 15.6886ZM16.7018 15.69L16.8966 14.9657L16.171 14.7706L15.9772 15.4966L16.7018 15.69ZM20 16.5769L19.8052 17.3012L21.5515 17.7708L20.6502 16.2031L20 16.5769ZM16.9483 11.2692L17.5985 10.8954L17.1552 10.1244L16.4701 10.6915L16.9483 11.2692ZM13.1711 14.3957L12.6929 13.818L12.2056 14.2213L12.5209 14.7696L13.1711 14.3957ZM15.8185 19L15.1683 19.3738L16.0744 20.9497L16.5431 19.1934L15.8185 19ZM17.6179 10.9288C17.4114 10.5697 16.953 10.4459 16.5939 10.6524C16.2348 10.8588 16.111 11.3173 16.3175 11.6764L17.6179 10.9288ZM13.8407 14.054C13.6343 13.6948 13.1758 13.5711 12.8167 13.7775C12.4576 13.9839 12.3338 14.4423 12.5402 14.8014L13.8407 14.054ZM17.858 7.88971H18.608H17.858ZM4.13369 7.88971H3.38369H4.13369ZM10.9958 1V0.25V1ZM11.2769 4.14875L11.9491 3.81598L10.5269 4.14875H11.2769ZM12.2226 6.05885L12.91 5.75894L12.9028 5.74232L12.8947 5.72608L12.2226 6.05885ZM12.4635 6.2354L12.5764 5.49394L12.5588 5.49126L12.541 5.48942L12.4635 6.2354ZM14.564 6.55514L14.4511 7.2966L14.4598 7.29792L14.4685 7.29904L14.564 6.55514ZM14.7398 7.10704L14.2295 6.5574L14.2227 6.56386L14.7398 7.10704ZM13.1877 8.5848L12.6705 8.04161L12.581 8.12682L12.5236 8.23623L13.1877 8.5848ZM13.1877 8.87257L13.9302 8.76712L13.912 8.63878L13.8518 8.524L13.1877 8.87257ZM13.4854 10.969L12.7428 11.0744L12.7464 11.0998L12.7518 11.125L13.4854 10.969ZM13.0118 11.3109L13.3947 10.666L13.3794 10.6569L13.3636 10.6485L13.0118 11.3109ZM11.1426 10.3183L11.4944 9.65595L11.4735 9.64487L11.452 9.63513L11.1426 10.3183ZM10.8352 10.3183L10.5259 9.63513L10.5044 9.64487L10.4835 9.65595L10.8352 10.3183ZM8.966 11.3109L8.61426 10.6485L8.60229 10.6549L8.59056 10.6617L8.966 11.3109ZM8.49523 10.969L9.22973 11.1206L9.23257 11.1069L9.23489 11.093L8.49523 10.969ZM8.84692 8.87257L8.13103 8.64895L8.11574 8.6979L8.10726 8.74848L8.84692 8.87257ZM8.79154 8.5848L9.37297 8.11105L9.34402 8.07552L9.31096 8.04378L8.79154 8.5848ZM7.24077 7.09592L6.70934 7.62515L6.71528 7.63111L6.72134 7.63694L7.24077 7.09592ZM7.418 6.55375L7.30945 5.81163L7.30458 5.81238L7.418 6.55375ZM9.51846 6.24652L9.44099 5.50054L9.42541 5.50215L9.40992 5.50442L9.51846 6.24652ZM9.75939 6.06997L9.08735 5.737L9.07925 5.75334L9.07196 5.77006L9.75939 6.06997ZM10.7051 4.16126L11.3771 4.49423L11.3813 4.48572L11.3853 4.47712L10.7051 4.16126ZM11.2769 4.15014L10.6094 4.49204L12.0269 4.15014H11.2769ZM5.68522 11.6723C5.89173 11.3133 5.76806 10.8548 5.409 10.6483C5.04994 10.4418 4.59145 10.5654 4.38494 10.9245L5.68522 11.6723ZM6.18154 19L5.45696 19.1936C5.53535 19.487 5.78295 19.7042 6.08408 19.7436C6.3852 19.7831 6.68041 19.6371 6.83176 19.3738L6.18154 19ZM9.46253 14.7973C9.66896 14.4382 9.5452 13.9797 9.18609 13.7733C8.82698 13.5669 8.36852 13.6907 8.16209 14.0498L9.46253 14.7973ZM5.10221 14.9657L1.80544 15.8512L2.19456 17.2999L5.49133 16.4143L5.10221 14.9657ZM2.65019 16.9494L5.70188 11.6417L4.4015 10.894L1.34981 16.2017L2.65019 16.9494ZM4.57347 11.8456L8.3507 14.9721L9.30715 13.8166L5.52992 10.6901L4.57347 11.8456ZM8.17874 14.0205L5.53135 18.6248L6.83172 19.3725L9.47911 14.7682L8.17874 14.0205ZM6.9061 18.8049L6.02133 15.4949L4.57221 15.8823L5.45698 19.1923L6.9061 18.8049ZM4.54677 15.6886V15.69H6.04677V15.6886H4.54677ZM16.5071 16.4143L19.8052 17.3012L20.1948 15.8527L16.8966 14.9657L16.5071 16.4143ZM20.6502 16.2031L17.5985 10.8954L16.2981 11.6431L19.3498 16.9508L20.6502 16.2031ZM16.4701 10.6915L12.6929 13.818L13.6493 14.9735L17.4265 11.847L16.4701 10.6915ZM12.5209 14.7696L15.1683 19.3738L16.4686 18.6262L13.8213 14.0219L12.5209 14.7696ZM16.5431 19.1934L17.4265 15.8834L15.9772 15.4966L15.0938 18.8066L16.5431 19.1934ZM16.3175 11.6764L19.3498 16.9507L20.6502 16.2031L17.6179 10.9288L16.3175 11.6764ZM16.4687 18.6263L13.8407 14.054L12.5402 14.8014L15.1682 19.3737L16.4687 18.6263ZM17.108 7.88971C17.108 9.51903 16.4633 11.081 15.3167 12.2322L16.3795 13.2907C17.8067 11.8578 18.608 9.91492 18.608 7.88971H17.108ZM15.3167 12.2322C14.1702 13.3833 12.6159 14.0294 10.9958 14.0294V15.5294C13.0157 15.5294 14.9522 14.7238 16.3795 13.2907L15.3167 12.2322ZM10.9958 14.0294C9.37577 14.0294 7.82145 13.3833 6.67496 12.2322L5.61218 13.2907C7.0395 14.7238 8.97601 15.5294 10.9958 15.5294V14.0294ZM6.67496 12.2322C5.52838 11.081 4.88369 9.51903 4.88369 7.88971H3.38369C3.38369 9.91492 4.18496 11.8578 5.61218 13.2907L6.67496 12.2322ZM4.88369 7.88971C4.88369 6.26039 5.52838 4.69841 6.67496 3.54722L5.61218 2.48868C4.18496 3.92164 3.38369 5.8645 3.38369 7.88971H4.88369ZM6.67496 3.54722C7.82145 2.39613 9.37577 1.75 10.9958 1.75V0.25C8.97601 0.25 7.0395 1.05563 5.61218 2.48868L6.67496 3.54722ZM10.9958 1.75C12.6159 1.75 14.1702 2.39613 15.3167 3.54722L16.3795 2.48868C14.9522 1.05563 13.0157 0.25 10.9958 0.25V1.75ZM15.3167 3.54722C16.4633 4.69841 17.108 6.26039 17.108 7.88971H18.608C18.608 5.8645 17.8067 3.92164 16.3795 2.48868L15.3167 3.54722ZM10.6048 4.48152L11.5505 6.39162L12.8947 5.72608L11.9491 3.81598L10.6048 4.48152ZM11.5352 6.35877C11.6091 6.52815 11.7266 6.67522 11.876 6.78474L12.7627 5.57483C12.8274 5.62231 12.8782 5.68592 12.91 5.75894L11.5352 6.35877ZM11.876 6.78474C12.0255 6.89426 12.2015 6.96222 12.3861 6.98139L12.541 5.48942C12.6214 5.49776 12.6978 5.52733 12.7627 5.57483L11.876 6.78474ZM12.3507 6.97686L14.4511 7.2966L14.6769 5.81368L12.5764 5.49394L12.3507 6.97686ZM14.4685 7.29904C14.3865 7.28851 14.3094 7.25473 14.2464 7.20205L15.2083 6.05106C15.0521 5.92054 14.8617 5.83721 14.6595 5.81125L14.4685 7.29904ZM14.2464 7.20205C14.1834 7.14939 14.137 7.08011 14.1123 7.00257L15.5415 6.54719C15.4799 6.35375 15.3644 6.18156 15.2083 6.05106L14.2464 7.20205ZM14.1123 7.00257C14.0876 6.92504 14.0855 6.84214 14.1061 6.76348L15.5571 7.14392C15.6085 6.94758 15.6032 6.74062 15.5415 6.54719L14.1123 7.00257ZM14.1061 6.76348C14.1267 6.68481 14.1694 6.61326 14.2295 6.55744L15.2502 7.65664C15.3993 7.5182 15.5056 7.34027 15.5571 7.14392L14.1061 6.76348ZM14.2227 6.56386L12.6705 8.04161L13.7048 9.12799L15.257 7.65023L14.2227 6.56386ZM12.5236 8.23623C12.4438 8.38821 12.4022 8.55722 12.4022 8.72868H13.9022C13.9022 8.79986 13.885 8.87011 13.8518 8.93337L12.5236 8.23623ZM12.4022 8.72868C12.4022 8.90015 12.4438 9.06916 12.5236 9.22113L13.8518 8.524C13.885 8.58726 13.9022 8.6575 13.9022 8.72868H12.4022ZM12.4451 8.97801L12.7428 11.0744L14.2279 10.8635L13.9302 8.76712L12.4451 8.97801ZM12.7518 11.125C12.7345 11.0438 12.7412 10.9593 12.771 10.8817L14.1711 11.4201C14.2455 11.2266 14.2621 11.0156 14.219 10.813L12.7518 11.125ZM12.771 10.8817C12.8008 10.8041 12.8527 10.7364 12.9208 10.6873L13.799 11.9033C13.9675 11.7816 14.0966 11.6136 14.1711 11.4201L12.771 10.8817ZM12.9208 10.6873C12.9889 10.6381 13.0701 10.6097 13.1545 10.6059L13.2219 12.1044C13.4299 12.095 13.6305 12.025 13.799 11.9033L12.9208 10.6873ZM13.1545 10.6059C13.2389 10.6021 13.3224 10.6231 13.3947 10.666L12.629 11.9558C12.8078 12.062 13.0139 12.1137 13.2219 12.1044L13.1545 10.6059ZM13.3636 10.6485L11.4944 9.65595L10.7909 10.9808L12.6601 11.9733L13.3636 10.6485ZM11.452 9.63513C11.3065 9.56927 11.1487 9.53516 10.9889 9.53516V11.0352C10.9351 11.0352 10.8821 11.0237 10.8333 11.0016L11.452 9.63513ZM10.9889 9.53516C10.8292 9.53516 10.6713 9.56927 10.5259 9.63513L11.1446 11.0016C11.0958 11.0237 11.0427 11.0352 10.9889 11.0352V9.53516ZM10.4835 9.65595L8.61426 10.6485L9.31774 11.9733L11.187 10.9808L10.4835 9.65595ZM8.59056 10.6617C8.66283 10.6199 8.74587 10.5998 8.8296 10.6042L8.75148 12.1022C8.95785 12.1129 9.16275 12.0635 9.34144 11.9602L8.59056 10.6617ZM8.8296 10.6042C8.91334 10.6086 8.99375 10.6371 9.06118 10.6861L8.17959 11.8997C8.3465 12.021 8.5451 12.0914 8.75148 12.1022L8.8296 10.6042ZM9.06118 10.6861C9.12857 10.7351 9.18002 10.8022 9.20976 10.8792L7.81054 11.4198C7.88469 11.6117 8.0127 11.7785 8.17959 11.8997L9.06118 10.6861ZM9.20976 10.8792C9.23949 10.9562 9.24639 11.04 9.22973 11.1206L7.76073 10.8173C7.71914 11.0187 7.73641 11.2279 7.81054 11.4198L9.20976 10.8792ZM9.23489 11.093L9.58659 8.99665L8.10726 8.74848L7.75557 10.8449L9.23489 11.093ZM9.56281 9.09618C9.615 8.92912 9.62498 8.75173 9.59191 8.57991L8.11894 8.86341C8.10521 8.79205 8.10935 8.71838 8.13103 8.64895L9.56281 9.09618ZM9.59191 8.57991C9.55884 8.40808 9.48369 8.24694 9.37297 8.11105L8.21011 9.05855C8.16392 9.00186 8.13268 8.93478 8.11894 8.86341L9.59191 8.57991ZM9.31096 8.04378L7.76019 6.55491L6.72134 7.63694L8.27211 9.12582L9.31096 8.04378ZM7.77219 6.56669C7.82841 6.62314 7.86763 6.69381 7.88589 6.7707L6.4265 7.11734C6.47208 7.30925 6.56985 7.48509 6.70934 7.62515L7.77219 6.56669ZM7.88589 6.7707C7.90415 6.84758 7.90084 6.92801 7.87627 7.00317L6.45051 6.5371C6.38921 6.72462 6.38091 6.92543 6.4265 7.11734L7.88589 6.7707ZM7.87627 7.00317C7.85169 7.07835 7.8067 7.14562 7.74591 7.19729L6.77453 6.0543C6.62384 6.18237 6.51181 6.34957 6.45051 6.5371L7.87627 7.00317ZM7.74591 7.19729C7.68509 7.24898 7.61087 7.28297 7.53142 7.29513L7.30458 5.81238C7.10869 5.84235 6.92524 5.92622 6.77453 6.0543L7.74591 7.19729ZM7.52655 7.29586L9.62701 6.98863L9.40992 5.50442L7.30945 5.81165L7.52655 7.29586ZM9.59594 6.99251C9.78054 6.97334 9.95653 6.90538 10.106 6.79586L9.21934 5.58595C9.28415 5.53846 9.36062 5.50888 9.44099 5.50054L9.59594 6.99251ZM10.106 6.79586C10.2554 6.68635 10.3729 6.53928 10.4468 6.36989L9.07196 5.77006C9.10382 5.69704 9.15456 5.63343 9.21934 5.58595L10.106 6.79586ZM10.4314 6.40294L11.3771 4.49423L10.033 3.82829L9.08735 5.737L10.4314 6.40294ZM11.3853 4.47712C11.3516 4.54981 11.298 4.61197 11.2303 4.65622L10.4097 3.40054C10.2422 3.51005 10.1089 3.66428 10.0248 3.8454L11.3853 4.47712ZM11.2303 4.65622C11.1625 4.70049 11.0835 4.72496 11.0021 4.72654L10.973 3.22682C10.7725 3.23072 10.5774 3.29101 10.4097 3.40054L11.2303 4.65622ZM11.0021 4.72654C10.9208 4.72812 10.8408 4.70674 10.7714 4.66511L11.5426 3.37854C11.3708 3.27558 11.1734 3.22292 10.973 3.22682L11.0021 4.72654ZM10.7714 4.66511C10.7019 4.62349 10.6459 4.56341 10.6094 4.49204L11.9445 3.80824C11.8534 3.63047 11.7143 3.48148 11.5426 3.37854L10.7714 4.66511ZM12.0269 4.15014V4.14875H10.5269V4.15014H12.0269ZM4.38494 10.9245L1.34986 16.2016L2.65014 16.9495L5.68522 11.6723L4.38494 10.9245ZM2.19485 17.2998L5.49161 16.4128L5.10192 14.9644L1.80515 15.8513L2.19485 17.2998ZM4.57219 15.8822L5.45696 19.1936L6.90612 18.8064L6.02135 15.495L4.57219 15.8822ZM6.83176 19.3738L9.46253 14.7973L8.16209 14.0498L5.53132 18.6262L6.83176 19.3738Z" fill="url(#paint0_linear_12936_1064)"/>
      <defs>
      <linearGradient id="paint0_linear_12936_1064" x1="-11.52" y1="21.4545" x2="20.2271" y2="20.775" gradientUnits="userSpaceOnUse">
      <stop stop-color="#92C127"/>
      <stop offset="1" stop-color="#008C95"/>
      </linearGradient>
      </defs>
    </svg>
  </div>
</template>

<style scoped lang="scss">
.avatar {
  position: relative;

  >svg {
    position: absolute;
    z-index: 1000;
    left: calc(100% - 3px);
    top: 0;
    transform: translateY(-35%);
  }

  &.ambassador-avatar {
    .photo, .no-photo {
      border: none !important;
      position: relative;
    }
    &::before {
      pointer-events: none;
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      height: calc(100% + 4px);
      width: calc(100% + 4px);
      background: linear-gradient(white, white) padding-box, linear-gradient(to right, #92C127, #008c95) border-box;
      border-radius: 50em;
      border: 4px solid transparent;
    }
  }

  .no-photo {
    border-color: transparent !important;
  }

  .photo {
    border-radius: var(--radius-full);
    border: 1px solid var(--old-100);

    background-color: var(--old-50);
    color: var(--old-500);

    display: flex;
    align-items: center;
    justify-content: center;

    font-weight: var(--weight-semibold);
    text-transform: capitalize;
    object-fit: cover;
  }

  img {
    animation: show 0.3s both;
  }

  @keyframes show {
    0% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }

  .initials {
    user-select: none;
    z-index: 999;
  }
}

.avatar-sm {
  width: var(--w-8);
  height: var(--h-8);

  font-size: var(--font-xs);
}

.avatar-xsm {
  width: var(--w-10);
  height: var(--h-10);

  font-size: var(--font-xs);
}

.avatar-md {
  width: var(--w-12);
  height: var(--h-12);

  font-size: var(--font-base);
}

.avatar-lg {
  width: var(--w-16);
  height: var(--h-16);

  font-size: var(--font-xl);
}

.avatar-xl {
  width: var(--w-24);
  height: var(--h-24);

  font-size: var(--font-3xl);
}
</style>
